import { Component, OnInit, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { db, AssertionIssue, Assertion, Media } from "../../../../../core/database/db";
import ScheduleDetailHeader from "../../../../../domains/jobs/components/shared/schedule-detail-header/schedule-detail-header.component";
import { AssertionImageGalleryComponent } from "../../../../../domains/media/components/assertion-image-gallery/assertion-image-gallery.component";
import { BlobService } from "../../../../../shared/services/blob.service";
import { LoggerService } from "../../../../../shared/services/logger.service";

export interface OnlineImages {
  azureBlobUri?: string;
  mediaGUID?: string;
}
interface ExtendedAssertion extends Assertion {
  jobId: number;
  areaName: string;
  componentName: string;
  media?: Media[]
  issues?: AssertionIssue[];
}

interface ExtendedIssue extends AssertionIssue {
  media?: Media[]
}
@Component({
  selector: "app-issue-detail",
  standalone: true,
  imports: [CommonModule, ScheduleDetailHeader, AssertionImageGalleryComponent],
  templateUrl: "./issue-detail.component.html",
  styleUrls: ["./issue-detail.component.scss"],
})
export default class IssueDetailComponent implements OnInit, OnDestroy {
  issue: ExtendedIssue | null = null;
  assertion: ExtendedAssertion | null = null;
  assertionImages: (Media & OnlineImages)[] = [];
  issueImages: (Media & OnlineImages)[] = [];
  loading = true;
  jobId?: number;
  onlineView: boolean = false;
  private blobUrls: string[] = [];
  private blobUrlCache = new Map<string, string>();

  isShowingAssertionOnly = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private blobService: BlobService,
    private logger: LoggerService
  ) {
    const extras = router.getCurrentNavigation()?.extras;
    if (extras && extras.state) {

      if (extras.state["issue"]) {
        const issue = extras.state["issue"];
        const assertion = {
          id: issue.assertionId,
          text: issue.assertionText,
          componentName: issue.componentName,
          areaName: issue.areaName
        };

        this.assertion = assertion as unknown as ExtendedAssertion;
        this.issue = issue;
      } else {
        this.isShowingAssertionOnly = true;
        this.assertion = extras.state["assertion"];
      }
      this.onlineView = true;
    }

  }

  async ngOnInit(): Promise<void> {
    if (this.onlineView) {
      await this.setupOnlineData();
    } else {
      await this.setupOfflineData();
    }


    this.loading = false;
  }

  async setupOnlineData() {
    this.jobId = this.isShowingAssertionOnly ? this.assertion?.jobId : this.issue?.jobId;

    if (this.isShowingAssertionOnly) {
      const enhancedMedia = await this.hydrateMediaImages(this.assertion!.media!);
      this.assertionImages = this.processImages(enhancedMedia);
    } else {
      const enhancedMedia = await this.hydrateMediaImages(this.issue!.media!);
      this.issueImages = this.processImages(enhancedMedia);
    }
  }
  async setupOfflineData() {
    const issueId = Number(this.route.snapshot.paramMap.get("issueId"));
    const assertionId = Number(this.route.snapshot.paramMap.get("assertionId"));
    this.jobId = Number(this.route.snapshot.paramMap.get("jobId"));

    if (assertionId) {
      await this.loadAssertion(assertionId);

      if (issueId) {
        await this.loadIssue(issueId);
        await this.loadIssueImages(issueId);
        this.isShowingAssertionOnly = false;
      } else {
        await this.loadAssertionImages(assertionId);
        this.isShowingAssertionOnly = true;
      }
    }
  }
  private async loadIssue(issueId: number): Promise<void> {
    try {
      const issue = await db.assertionIssue.get(issueId);
      this.issue = issue || null;
      if (!this.issue) {
        this.logger.warn("Issue not found");
      }
    } catch (error) {
      this.logger.error("Error loading issue", error);
      this.issue = null;
    }
  }

  private async loadAssertion(assertionId: number): Promise<void> {
    try {
      // Get the assertion
      const assertion = await db.assertion.get(assertionId);
      if (!assertion) {
        this.logger.warn("Assertion not found");
        return;
      }

      // Get the component
      const component = await db.component.get(assertion.componentId);
      if (!component) {
        this.logger.warn("Component not found");
        return;
      }

      // Get the area
      const area = await db.area.get(component.areaId);
      if (!area) {
        this.logger.warn("Area not found");
        return;
      }

      // Get the zone
      const zone = await db.zone.get(area.zoneId);
      if (!zone) {
        this.logger.warn("Zone not found");
        return;
      }

      const issues = await db.assertionIssue.where("assertionIdentifier").equals(assertion.identifier)
        .toArray();

      this.assertion = {
        ...assertion,
        jobId: this.jobId ?? 0,
        issues,
        areaName: area.name,
        componentName: component.name
      };
    } catch (error) {
      this.logger.error("Error loading assertion", error);
    }
  }

  private async loadAssertionImages(assertionId: number): Promise<void> {
    try {
      const assertion = await db.assertion.get(assertionId);
      if (!assertion) {
        this.logger.warn("Assertion not found for images");
        this.assertionImages = [];
        return;
      }

      const mediaRecords = await db.media
        .where("parentIdentifier")
        .equals(assertion.identifier)
        .and(media =>
          media.deleted !== 1)
        .toArray();

      const enhancedMediaRecords = await this.hydrateOfflineMediaBlobs(mediaRecords);
      this.assertionImages = this.processOfflineImages(enhancedMediaRecords);
    } catch (error) {
      this.logger.warn("Error loading assertion images", error);
      this.assertionImages = [];
    }
  }

  private async loadIssueImages(issueId: number): Promise<void> {
    try {
      const issue = await db.assertionIssue.get(issueId);
      if (!issue) {
        this.logger.warn("Issue not found for images");
        this.issueImages = [];
        return;
      }

      const mediaRecords = await db.media
        .where("parentIdentifier")
        .equals(issue.identifier)
        .and(media => media.deleted !== 1)
        .toArray();

      const enhancedMediaRecords = await this.hydrateOfflineMediaBlobs(mediaRecords);
      this.issueImages = this.processOfflineImages(enhancedMediaRecords);
    } catch (error) {
      this.logger.warn("Error loading issue images", error);
      this.issueImages = [];
    }
  }

  viewIssue(issue: AssertionIssue): void {
    if (!this.assertion || !this.jobId || !issue.id) {
      return;
    }

    this.router.navigate([
      "/ccm/bq7/job", this.jobId,
      "report",
      "issue", issue.id,
      "assertion", this.assertion.id
    ]);
  }

  breadcrumb(): string {
    if (!this.assertion) {
      return "";
    }
    return [this.assertion.areaName, this.assertion.componentName].filter(Boolean).join(" > ");
  }

  goBack(): void {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      this.router.navigate(["/ccm/bq7/job", this.jobId, "report"]);
    }
  }

  ngOnDestroy(): void {
    this.cleanupBlobUrls();
  }

  private cleanupBlobUrls(): void {
    this.blobUrlCache.clear();
    this.blobUrls.forEach(url => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        this.logger.error(`Failed to revoke blob URL: ${url}`, e);
      }
    });
    this.blobUrls = [];
  }

  private getCacheKey(image: Media & OnlineImages): string {
    const fid = image.fileIdentifier || image.mediaGUID || "";
    const updatedAtMs = image.updatedAt ? new Date(image.updatedAt).getTime() : 0;
    return `${fid}:${updatedAtMs}`;
  }

  private assignCachedBlobUrl(image: Media & OnlineImages): void {
    if (!image.base64Image) {
      return;
    }

    const key = this.getCacheKey(image);
    let url = this.blobUrlCache.get(key);

    if (!url) {
      url = URL.createObjectURL(this.blobService.base64ToBlob(image.base64Image));
      this.blobUrlCache.set(key, url);
      if (!this.blobUrls.includes(url)) {
        this.blobUrls.push(url);
      }
    }
    image.resourceUri = url;
  }

  private async hydrateMediaImages(
    mediaArray: (Media & OnlineImages)[]
  ): Promise<(Media & OnlineImages)[]> {
    return Promise.all(mediaArray.map(async m => {
      if (m.base64Image) {
        return m;
      }

      const identifier = m.fileIdentifier || m.mediaGUID;
      if (identifier) {
        const hydratedMedia = await db.media
          .where("fileIdentifier")
          .equals(identifier)
          .and(x => !!x.base64Image && !x.deleted)
          .first();

        if (hydratedMedia?.base64Image) {
          return { ...m, base64Image: hydratedMedia.base64Image };
        }
      }

      return m;
    }));
  }

  private async hydrateOfflineMediaBlobs(mediaRecords: Media[]): Promise<Media[]> {
    return Promise.all(mediaRecords.map(async m => {
      if (m.base64Image) {
        return m;
      }

      if (m.fileIdentifier) {
        const hydreatedMedia = await db.media
          .where("fileIdentifier")
          .equals(m.fileIdentifier)
          .and(x => !!x.base64Image && !x.deleted)
          .first();

        if (hydreatedMedia?.base64Image) {
          return { ...m, base64Image: hydreatedMedia.base64Image };
        }
      }

      return m;
    }));
  }

  private processImages(enhancedMedia: (Media & OnlineImages)[]): (Media & OnlineImages)[] {
    const processedImages = enhancedMedia
      .filter(m => m.base64Image || m.resourceUri || m.azureBlobUri)
      .sort((a, b) => {
        const getSortValue = (media: Media): number => media.id || 0;

        const aValue = getSortValue(a);
        const bValue = getSortValue(b);

        return bValue - aValue;
      });

    for (const image of processedImages) {
      if (image.base64Image) {
        this.assignCachedBlobUrl(image);
      }
    }

    return processedImages;
  }

  private processOfflineImages(enhancedMedia: Media[]): Media[] {
    const processedImages = enhancedMedia
      .filter(m => m.base64Image || m.resourceUri)
      .sort((a, b) => {
        const getSortValue = (media: Media): number => media.id || 0;

        const aValue = getSortValue(a);
        const bValue = getSortValue(b);

        return bValue - aValue;
      });

    for (const image of processedImages as (Media & OnlineImages)[]) {
      if (image.base64Image) {
        this.assignCachedBlobUrl(image);
      }
    }

    return processedImages;
  }
}

